import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Edit3 } from "lucide-react";

const ROLES = [
  { v: "salesperson", l: "Salesperson" },
  { v: "bdc", l: "BDC Agent" },
  { v: "floor_manager", l: "Floor Manager" },
  { v: "sales_manager", l: "Sales Manager" },
  { v: "gsm", l: "GSM" },
];

export default function Roster() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", display_name: "", phone: "", role: "salesperson", departments: ["New"], shift_start: "09:00", shift_end: "19:00" });

  const refresh = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  };
  useEffect(() => { refresh(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", email: "", display_name: "", phone: "", role: "salesperson", departments: ["New"], shift_start: "09:00", shift_end: "19:00" });
    setOpen(true);
  };
  const startEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name, email: u.email, display_name: u.display_name || "", phone: u.phone || "",
      role: u.role, departments: u.departments || ["New"], shift_start: u.shift_start || "09:00", shift_end: u.shift_end || "19:00",
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/users/${editing.user_id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/users", form);
        toast.success("Salesperson added");
      }
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.user_id}`, { active: !u.active });
    refresh();
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <div className="flex items-baseline justify-between border-b border-white/10 pb-3 mb-6">
          <div>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight">Roster</h1>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mt-1">Salespeople & Managers</p>
          </div>
          <Button onClick={startCreate} className="bg-[#34C759] text-black hover:bg-[#2bb850] rounded-sm uppercase tracking-wider font-bold" data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Person
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {users.map(u => (
            <div key={u.user_id} className={`bg-[#141414] border ${u.active ? "border-white/10" : "border-white/5 opacity-50"} p-4 rounded-sm`} data-testid={`user-card-${u.user_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display text-xl font-bold uppercase tracking-wide">{u.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#34C759]">{u.role.replace("_", " ")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.user_id}`} />
                  <Button size="sm" variant="ghost" onClick={() => startEdit(u)} className="text-white/40 hover:text-white" data-testid={`edit-user-${u.user_id}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-white/50">{u.email}</div>
              {u.phone && <div className="text-xs text-white/50">{u.phone}</div>}
              <div className="flex flex-wrap gap-1 mt-2">
                {(u.departments || []).map(d => (
                  <span key={d} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-white/5 text-white/60">{d}</span>
                ))}
              </div>
              {u.shift_start && (
                <div className="text-[10px] uppercase tracking-widest text-white/30 mt-2">Shift: {u.shift_start} – {u.shift_end}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-white/10 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider text-xl">{editing ? "Edit Person" : "Add Person"}</DialogTitle>
            <DialogDescription className="text-white/50">Roster member details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs uppercase tracking-widest text-white/60">Full Name</Label>
              <Input className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-white/60">Display Name</Label>
              <Input className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} data-testid="user-display-input" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-white/60">Phone</Label>
              <Input className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="user-phone-input" /></div>
            <div className="col-span-2"><Label className="text-xs uppercase tracking-widest text-white/60">Email</Label>
              <Input disabled={!!editing} className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-white/60">Role</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  {ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs uppercase tracking-widest text-white/60">Departments (comma)</Label>
              <Input className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" value={(form.departments || []).join(", ")} onChange={e => setForm({ ...form, departments: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} data-testid="user-depts-input" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-white/60">Shift Start</Label>
              <Input className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" value={form.shift_start} onChange={e => setForm({ ...form, shift_start: e.target.value })} placeholder="09:00" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-white/60">Shift End</Label>
              <Input className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" value={form.shift_end} onChange={e => setForm({ ...form, shift_end: e.target.value })} placeholder="19:00" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#34C759] text-black hover:bg-[#2bb850] rounded-sm uppercase tracking-wider font-bold" data-testid="user-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
